import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, Eye, Shield, Ban, KeyRound, Trash2, UserPlus } from "lucide-react";
import { api, startImpersonation } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const rolePill = (role) => {
  const map = {
    admin: { color: "#dc2626", bg: "rgba(220,38,38,0.15)", label: "admin" },
    user:  { color: "#3b82f6", bg: "rgba(59,130,246,0.15)", label: "user" },
  };
  const m = map[role] || map.user;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase tracking-widest"
      style={{ color: m.color, borderColor: `${m.color}55`, background: m.bg }}
    >
      {m.label}
    </span>
  );
};

const tierPill = (tier) => (
  <span className="text-[9px] font-mono uppercase tracking-widest text-[#9ca3af] border border-[#1f2937] rounded px-1.5 py-0.5">
    {tier || "free"}
  </span>
);

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [imperBusy, setImperBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25, filter });
      if (search) params.set("search", search);
      const r = await api.get(`/admin/users?${params.toString()}`);
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filter]);

  const impersonate = async (u) => {
    if (u.role === "admin") {
      if (!window.confirm(`Impersonate another admin (${u.email})? Their view collapses to user-scope during the session.`)) return;
    }
    setImperBusy(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/impersonate`);
      startImpersonation(r.data.token, {
        target: r.data.target,
        impersonator: r.data.impersonator,
        exp_minutes: r.data.expires_in_minutes,
      });
      toast.success(`Now impersonating ${u.email}`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    } finally {
      setImperBusy(null);
    }
  };

  const toggleDisable = async (u) => {
    const nextDisabled = !u.disabled;
    if (!window.confirm(`${nextDisabled ? "Disable" : "Enable"} ${u.email}?`)) return;
    try {
      await api.put(`/admin/users/${u.id}`, { disabled: nextDisabled });
      toast.success(`User ${nextDisabled ? "disabled" : "enabled"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A new temp password will be shown once.`)) return;
    try {
      const r = await api.post(`/admin/users/${u.id}/reset-password`);
      window.prompt("Copy this temp password NOW — it won't be shown again:", r.data.temp_password);
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter">Users</h1>
          <div className="vx-label mt-1">{total} total</div>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <Input
            placeholder="Search email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            className="pl-8 bg-[#0a0e1a] border-[#1f2937] font-mono text-xs h-8"
            data-testid="users-search-input"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          className="bg-[#0a0e1a] border border-[#1f2937] text-xs font-mono text-[#e5e7eb] rounded h-8 px-2"
          data-testid="users-filter-select"
        >
          <option value="all">All</option>
          <option value="admin">Admins</option>
          <option value="user">Users</option>
          <option value="disabled">Disabled</option>
        </select>
        <Button
          onClick={() => { setPage(1); load(); }}
          size="sm"
          variant="outline"
          className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8"
          data-testid="users-search-btn"
        >
          Search
        </Button>
      </div>

      <div className="rounded-lg border border-[#1f2937] bg-[#111827] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#0a0e1a] text-[#6b7280]">
            <tr>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-right px-3 py-2">Accts</th>
              <th className="text-right px-3 py-2">Signals</th>
              <th className="text-right px-3 py-2">P&L</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table-body">
            {loading ? (
              <tr><td colSpan={9} className="p-6 text-center text-[#6b7280]">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-center text-[#6b7280]">No users match your filter.</td></tr>
            ) : users.map((u) => (
              <tr
                key={u.id}
                className="border-t border-[#1f2937] hover:bg-[#1f2937]/40"
                data-testid={`user-row-${u.id}`}
              >
                <td className="px-3 py-2 text-[#e5e7eb]">{u.email}</td>
                <td className="px-3 py-2 text-[#9ca3af]">{u.name}</td>
                <td className="px-3 py-2">{rolePill(u.role)}</td>
                <td className="px-3 py-2">{tierPill(u.tier)}</td>
                <td className="px-3 py-2 text-right">{u.accounts_count}</td>
                <td className="px-3 py-2 text-right">{u.signals_count}</td>
                <td className="px-3 py-2 text-right font-bold"
                    style={{ color: u.total_pnl_usd >= 0 ? "#10b981" : "#ef4444" }}>
                  {u.total_pnl_usd === 0 ? "—" : `$${u.total_pnl_usd.toFixed(2)}`}
                </td>
                <td className="px-3 py-2 text-center">
                  {u.disabled ? (
                    <span className="text-[9px] font-mono text-[#ef4444] border border-[#ef4444]/40 rounded px-1.5 py-0.5">disabled</span>
                  ) : (
                    <span className="text-[9px] font-mono text-[#10b981]">active</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <button
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                    className="p-1 rounded hover:bg-[#1f2937] text-[#3b82f6]" title="View"
                    data-testid={`view-btn-${u.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => impersonate(u)}
                    disabled={imperBusy === u.id || u.disabled}
                    className="p-1 rounded hover:bg-[#fbbf24]/10 text-[#fbbf24] disabled:opacity-30" title="Impersonate"
                    data-testid={`impersonate-btn-${u.id}`}
                  >
                    {imperBusy === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => resetPassword(u)}
                    className="p-1 rounded hover:bg-[#a78bfa]/10 text-[#a78bfa]" title="Reset password"
                    data-testid={`reset-pw-btn-${u.id}`}
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => toggleDisable(u)}
                    className="p-1 rounded hover:bg-[#ef4444]/10 text-[#ef4444]" title={u.disabled ? "Enable" : "Disable"}
                    data-testid={`toggle-disable-btn-${u.id}`}
                  >
                    <Ban className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center text-xs font-mono text-[#9ca3af]">
        <div>Page {page} of {Math.max(1, Math.ceil(total / 25))}</div>
        <div className="space-x-1">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="border-[#1f2937] hover:bg-[#1f2937] h-7 text-xs" data-testid="page-prev">Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page * 25 >= total}
                  className="border-[#1f2937] hover:bg-[#1f2937] h-7 text-xs" data-testid="page-next">Next</Button>
        </div>
      </div>
    </div>
  );
}
